import { getPlayerStatAverage } from './playerUtils';

/**
 * Calculates the over/under line for a player's stat
 * @param {Array} players - Array of player objects
 * @param {String} playerId - Player ID
 * @param {String} statCategory - Stat category
 * @returns {Number} Over/under line
 */
export const calculateOverUnderLine = (players, playerId, statCategory) => {
  if (!players || players.length === 0 || !playerId || !statCategory) return 0;
  
  // Get all games for this player
  const playerGames = players.filter(player => player.player_id === playerId);
  
  // Calculate the average over the last 5 games
  const average = getPlayerStatAverage(players, playerId, statCategory, 5);
  
  // Round to the nearest 0.5
  return Math.round(average * 2) / 2;
};

/**
 * Calculates the hit rate for a player's stat
 * @param {Array} games - Array of game objects
 * @param {String} playerName - Player name
 * @param {String} statCategory - Stat category
 * @param {Number} line - Over/under line
 * @returns {Object} Hit rate object with percentage and record
 */
export const calculateHitRate = (games, playerName, statCategory, line) => {
  if (!games || !games.length) return { percentage: 0, record: '0/0' };

  const hits = games.filter(game => {
    // Get the stat value using the same logic as the chart
    let value;
    switch(statCategory) {
      case 'performance_points':
        // Calculate PP for this game
        let pp = 0;
        // 4pts per point scored
        pp += (parseFloat(game['points']) || 0) * 4;
        // 10pts per try assist
        pp += (parseFloat(game['try_assists']) || 0) * 10;
        // 1pt per 10-meter run
        const runMeters = parseFloat(game['all_run_metres']) || 0;
        pp += Math.floor(runMeters / 10);
        // 5pts per line break
        pp += (parseFloat(game['line_breaks']) || 0) * 5;
        // 1pt per tackle
        pp += (parseFloat(game['tackles_made']) || 0);
        value = pp;
        break;
      case 'all_run_metres':
        value = game.all_run_metres;
        break;
      case 'tackles_made':
        value = game.tackles_made;
        break;
      case 'tries':
        value = game.tries;
        break;
      case 'points':
        value = game.points;
        break;
      case 'line_breaks':
        value = game.line_breaks;
        break;
      case 'tackle_breaks':
        value = game.tackle_breaks;
        break;
      case 'post_contact_metres':
        value = game.post_contact_metres;
        break;
      case 'kick_metres':
        value = game.kick_metres;
        break;
      default:
        value = game[statCategory];
    }
    
    // Convert value to number and compare with line
    value = value === '-' || value === '' ? 0 : parseFloat(value) || 0;
    return value > line;
  }).length;

  const total = games.length;
  const percentage = (hits / total) * 100;
  
  return {
    percentage,
    record: `${hits}/${total}`
  };
};

/**
 * Calculates the trend for a player's stat
 * @param {Array} players - Array of player objects
 * @param {String} playerId - Player ID
 * @param {String} statCategory - Stat category
 * @returns {String} Trend direction ('up', 'down', or 'neutral')
 */
export const calculateTrend = (players, playerId, statCategory) => {
  if (!players || players.length === 0 || !playerId || !statCategory) return 'neutral';
  
  const playerGames = players.filter(player => player.player_id === playerId);
  
  // Sort games by date (oldest first)
  const sortedGames = [...playerGames].sort((a, b) => new Date(a.date) - new Date(b.date));
  
  if (sortedGames.length < 3) return 'neutral';
  
  // Get the last 3 games
  const lastThreeGames = sortedGames.slice(-3);
  
  const values = lastThreeGames.map(game => parseFloat(game[statCategory]) || 0);
  
  // Check if the trend is consistently up or down
  if (values[0] < values[1] && values[1] < values[2]) {
    return 'up';
  } else if (values[0] > values[1] && values[1] > values[2]) {
    return 'down';
  } else {
    return 'neutral';
  }
};

/**
 * Calculates the value rating for a bet
 * @param {Number} line - Over/under line
 * @param {Number} average - Player's average for the stat
 * @returns {Number} Value rating (1-10)
 */
export const calculateValueRating = (line, average) => {
  if (line === undefined || average === undefined) return 0;
  
  // Calculate the difference as a percentage
  const difference = ((average - line) / line) * 100;
  
  // Map the difference to a 1-10 scale
  // A difference of -20% or less is a 1
  // A difference of +20% or more is a 10
  const rating = Math.round(((difference + 20) / 40) * 9) + 1;
  
  // Clamp the rating between 1 and 10
  return Math.max(1, Math.min(10, rating));
};

/**
 * Generates a recommendation for a bet
 * @param {Number} hitRate - Hit rate percentage
 * @param {Number} valueRating - Value rating
 * @param {String} trend - Trend direction
 * @returns {Object} Recommendation object with strength and text
 */
export const generateRecommendation = (hitRate, valueRating, trend) => {
  if (hitRate === undefined || valueRating === undefined || !trend) {
    return { strength: 'neutral', text: 'Not enough data' };
  }
  
  let strength = 'neutral';
  let text = '';
  
  // Determine strength based on hit rate and value rating
  if (hitRate >= 70 && valueRating >= 7) {
    strength = 'strong';
  } else if (hitRate >= 60 && valueRating >= 6) {
    strength = 'moderate';
  } else if (hitRate <= 30 && valueRating <= 4) {
    strength = 'avoid';
  } else {
    strength = 'neutral';
  }
  
  // Adjust based on trend
  if (trend === 'up' && (strength === 'moderate' || strength === 'neutral')) {
    strength = strength === 'moderate' ? 'strong' : 'moderate';
  } else if (trend === 'down' && strength !== 'avoid') {
    strength = strength === 'strong' ? 'moderate' : 'neutral';
  }
  
  // Generate text based on strength
  switch (strength) {
    case 'strong':
      text = 'Strong recommendation to bet';
      break;
    case 'moderate':
      text = 'Moderate recommendation to bet';
      break;
    case 'neutral':
      text = 'Neutral recommendation';
      break;
    case 'avoid':
      text = 'Recommendation to avoid';
      break;
    default:
      text = 'Not enough data';
  }
  
  return { strength, text };
}; 